import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import apiConfig from '../../config/apiConfig';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchForeignResource } from '../../apis/resources';
import { fetchEnum } from '../../apis/enum';
import Cookies from "js-cookie";
import { useParams } from 'react-router-dom';
import { apiFetch } from "../../apis/apiFetch";

import { useApplicantViewModel } from "../../viewModels/useApplicantViewModel";
import { useRaspStore } from '../../store/raspStore';
import { LoginContext } from '../../context/LoginContext';
import './FormStyles.css';
import { toast } from '../../utils/toast';
import { validate } from '../../utils/validator';
import { applicantValidationRules } from '../../validators/applicantValidator';
import RenderImage from '../QueryManager/RenderImage/RenderImage';

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

export interface ApplicantFormRef {
  save: () => Promise<Record<string, string>>;
  getErrors: () => Record<string, string>;
}

type CreateApplicantProps = {
  dataToSave: any;
  setDataToSave: React.Dispatch<React.SetStateAction<any>>;
};
// getCookie removed - using apiFetch

const CreateApplicant = forwardRef<ApplicantFormRef, CreateApplicantProps>((props, ref) => {
  const { dataToSave, setDataToSave } = props;
  const { user } = useContext(LoginContext); // logged-in user

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [showToast, setShowToast] = useState<any>(false);
  const [photoError, setPhotoError] = useState<string>("");
  const [preview, setPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const regex = /^(g_|archived|extra_data)/;
  const apiUrl = apiConfig.getResourceUrl("applications");
  const metadataUrl = apiConfig.getResourceMetaDataUrl("applications")

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const hasPrePopulated = useRef(false);
  const queryClient = useQueryClient();

  // getUserIdFromJWT removed - using LoginContext

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    // dataToSave,
    // setDataToSave,
    loadMetadata,
    save,
  } = useApplicantViewModel(user?.userId || "", appId);



  useEffect(() => {
    loadMetadata();
  }, [])

  // Auto-set user_id from logged-in user's email
  useEffect(() => {
    if (!user || !user.email_id) return;
    const users = foreignKeyData["Users"] || [];
    if (!users.length) return;

    console.log("LoginContext user:", user);
    console.log("Users data:", users);
    console.log("Looking for email:", user.email_id.toLowerCase());

    const match = users.find(
      (u: any) => {
        const userEmail = (u.email || u.email_id || u.user_email || "").toLowerCase().trim();
        const targetEmail = user.email_id.toLowerCase().trim();
        const isMatch = userEmail === targetEmail;
        if (isMatch) {
          console.log("MATCH FOUND:", u);
        }
        return isMatch;
      }
    );

    if (match && match.id) {
      console.log("Setting user_id to:", match.id);
      setDataToSave((prev: any) => ({
        ...prev,
        user_id: match.id,
        fullname: match.user_name || "", // Auto-populate name
        email: match.email || match.user_email, // Auto-populate email
        mobile: match.mobile_number || match.mobile, // Auto-populate mobile
      }));
    } else {
      console.warn("No matching user found for logged-in user email:", user.email_id);
      console.warn("Available users with emails:", users.map((u: any) => ({ id: u.id, user_email: u.user_email, email: u.email })));
    }
  }, [user, foreignKeyData]);

  // ✅ async function, not useQuery
  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  // ✅ async function, not useQuery
  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  // ✅ useQuery only here
  const { data: metaData, isLoading, error } = useQuery({
    queryKey: ['resMetaData', 'applicantCreate'],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter((field: any) => field.foreign);
      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ['foreignData', field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(field.foreign, field.name, field.foreign_field);
        }
      }

      const enumFields = data[0].fieldValues.filter((field: any) => field.isEnum === true);
      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ['enum', field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });


  useEffect(() => {
    console.log("data to save", dataToSave)
  }, [dataToSave])


  const { data: dataRes, isLoading: isLoadingDataRes, error: errorDataRes } = useQuery({
    queryKey: ['resourceData', 'applicantCreate', user?.userId, localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      if (!user?.userId) return [];
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return data.resource || [];
    },
    enabled: !!user?.userId
  })
  useEffect(() => {
    if (dataRes) {
      let dataToStore = { resource: 'applicant', records: dataRes }
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
    }
  }, [dataRes, user])

  // Pre-populate form with existing data when user_id is resolved and records are loaded
  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (!hasPrePopulated.current && dataRes && dataRes.length > 0 && dataToSave.user_id && activeDriveId) {
      const userRecord = dataRes.find((r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId);
      if (userRecord) {
        hasPrePopulated.current = true;
        // Merge userRecord into dataToSave, ensuring id and drive_id are preserved
        setDataToSave((prev: any) => ({ ...userRecord, ...prev }));
      }
    }
  }, [dataRes, dataToSave.user_id])

  const handleCreate = async () => {
    const validationErrors = validate(dataToSave, applicantValidationRules);
    setErrors(validationErrors);
    
    // Always save as draft regardless of validation for better UX
    await save(dataToSave);
    queryClient.invalidateQueries({ queryKey: ['resourceData', 'applicantCreate'] });
    
    return validationErrors;
  };

  // Expose save method to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleCreate,
    getErrors: () => errors
  }));


  const handleSearchChange = (fieldName: string, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [fieldName]: value }));
  };

  return (
    <div className="form-container">
      <h3 className="form-subtitle">Profile</h3>

      <div className="form-grid">
        {/* Full Name */}
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input
            type="text"
            className={`form-input ${errors.fullname ? "input-error" : ""}`}
            name="fullname"
            value={dataToSave["fullname"] || ""}
            placeholder="Full Name"
            readOnly
            style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
          />
          {errors.fullname && <span className="error-text">{errors.fullname}</span>}
        </div>

        {/* Email */}
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input
            type="email"
            className={`form-input ${errors.email ? "input-error" : ""}`}
            name="email"
            value={dataToSave["email"] || ""}
            placeholder="Email"
            readOnly
            style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        {/* Mobile */}
        <div className="form-group">
          <label className="form-label">Mobile *</label>
          <input
            type="text"
            className={`form-input ${errors.mobile ? "input-error" : ""}`}
            name="mobile"
            value={dataToSave["mobile"] || ""}
            placeholder="Mobile"
            readOnly
            style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
          />
          {errors.mobile && <span className="error-text">{errors.mobile}</span>}
        </div>

        {/* Date of Birth */}
        <div className="form-group">
          <label className="form-label">Date of Birth *</label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD-MM-YYYY"
              value={dataToSave["dob"] ? dayjs(dataToSave["dob"]) : null}
              maxDate={dayjs()}
              onChange={(newValue) => {
                setDataToSave((prev: any) => ({
                  ...prev,
                  dob: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                }));
                if (errors.dob) setErrors((prev: any) => ({ ...prev, dob: "" }));
              }}
              slotProps={{
                textField: {
                  placeholder: "dd-mm-yyyy",
                  fullWidth: true,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                       padding: '0px 14px',
                       backgroundColor: errors.dob ? '#fff5f5' : '#fff',
                       borderRadius: '4px',
                       "& fieldset": {
                         borderColor: errors.dob ? '#dc2626' : '#d0d0d0',
                         borderWidth: '1px',
                         transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                       },
                       "&:hover fieldset": {
                         borderColor: errors.dob ? '#dc2626' : '#d0d0d0',
                       },
                       "&.Mui-focused fieldset": {
                         borderColor: errors.dob ? '#dc2626' : '#2196f3',
                         boxShadow: errors.dob ? '0 0 0 2px rgba(220, 38, 38, 0.2)' : '0 0 0 2px rgba(33, 150, 243, 0.1)',
                         borderWidth: '1px'
                       }
                    },
                    "& .MuiOutlinedInput-input": {
                       padding: '10px 0px',
                       fontSize: '14px',
                       color: '#333',
                       fontFamily: 'inherit',
                       height: 'auto',
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
          {errors.dob && <span className="error-text">{errors.dob}</span>}
        </div>

        {/* Gender */}
        <div className="form-group">
          <label className="form-label">Gender *</label>
          <select
            className={`form-select ${errors.gender ? "input-error" : ""}`}
            name="gender"
            value={dataToSave["gender"] || ""}
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, gender: e.target.value }));
              if (errors.gender) setErrors(prev => ({ ...prev, gender: "" }));
            }}
          >
            <option value="">Select Gender</option>
            {Object.keys(enums).length !== 0 && enums["Gender"] !== undefined &&
              enums["Gender"]?.filter((val: any) => val !== "Others" && val !== "Other").map((val: any, idx: any) => (
                <option key={idx} value={val}>{val}</option>
              ))}
          </select>
          {errors.gender && <span className="error-text">{errors.gender}</span>}
        </div>

        {/* Nationality */}
        <div className="form-group">
          <label className="form-label">Nationality *</label>

          <select
            className={`form-select ${errors.nationality ? "input-error" : ""}`}
            name="nationality"
            value={dataToSave["nationality"] === "Indian" ? "Indian" : (dataToSave["nationality"] !== undefined && dataToSave["nationality"] !== null && dataToSave["nationality"] !== "" ? "Other" : (dataToSave["nationality"] === "" ? "Other" : ""))}
            onChange={(e) => {
              if (errors.nationality) setErrors(prev => ({ ...prev, nationality: "" }));
              if (e.target.value === "Indian") {
                setDataToSave((prev: any) => ({ ...prev, nationality: "Indian" }));
              } else if (e.target.value === "Other") {
                setDataToSave((prev: any) => ({ ...prev, nationality: "" }));
              } else {
                setDataToSave((prev: any) => ({ ...prev, nationality: undefined }));
              }
            }}
          >
            <option value="">Select Nationality</option>
            <option value="Indian">Indian</option>
            <option value="Other">Other</option>
          </select>
          {errors.nationality && <span className="error-text">{errors.nationality}</span>}
        </div>


        {dataToSave["nationality"] !== "Indian" && dataToSave["nationality"] !== undefined && dataToSave["nationality"] !== null && (
          <div className="form-group">
            <label className="form-label">Specify Nationality *</label>
            <input
              type="text"
              className={`form-input ${errors.nationality ? "input-error" : ""}`}
              name="nationality"
              placeholder="Enter Nationality"
              value={dataToSave["nationality"] || ""}
              onChange={(e) => {
                setDataToSave((prev: any) => ({
                  ...prev,
                  nationality: e.target.value,
                }));
                if (errors.nationality) setErrors(prev => ({ ...prev, nationality: "" }));
              }}
            />
            {errors.nationality && <span className="error-text">{errors.nationality}</span>}
          </div>
        )}

        {/* Photo */}
        <div className="form-group">
          <>
            <label className="form-label">Photo *</label>
            <input
              className={`form-input ${errors.photo ? "input-error" : ""}`}
              type="file"
              name="photo"
              accept="image/jpeg,image/png,image/jpg, application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (file && file.size > 512 * 1024) {
                  setPhotoError("Photo size must be less than 512 KB. Please choose a smaller file.");
                  e.target.value = "";
                  return;
                }
                setPhotoError("");
                setDataToSave((prev: any) => ({ ...prev, photo: file }));
                if (file) {
                  const objectUrl = URL.createObjectURL(file);
                  setPreview(objectUrl);
                }
                if (errors.photo) setErrors(prev => ({ ...prev, photo: "" }));
              }}
            />
            {photoError && <span style={{ color: "red", fontSize: "0.8rem", marginTop: "4px", display: "block" }}>{photoError}</span>}
            {errors.photo && <span className="error-text">{errors.photo}</span>}
            <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
              Max size: 512 KB | Resolution: e.g. 200x200 pixels | Formats: JPG, JPEG, PNG, PDF
            </small>
          </>
          {preview ?
            <div style={{ display: "flex", justifyContent: "flex-start", marginLeft: 100, marginTop: 10 }}>
              <img src={preview} alt="preview" style={{ maxWidth: '40%' }} />
            </div> :
            <div>
              <small style={{ color: "#1d1e1f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Previous Uploaded File:
              </small>
              <RenderImage
                photo={dataToSave["photo"]}
              />
            </div>
          }
        </div>

        {/* ABC - ID */}
        <div className="form-group">
          <label className="form-label">ABC ID *</label>
          <input
            type="text"
            className={`form-input ${errors.abc_id ? "input-error" : ""}`}
            name="abc_id"
            value={dataToSave["abc_id"] || ""}
            placeholder="ABC ID"
            onChange={(e) => {
              setDataToSave({ ...dataToSave, abc_id: e.target.value });
              if (errors.abc_id) setErrors(prev => ({ ...prev, abc_id: "" }));
            }}
          />
          {errors.abc_id && <span className="error-text">{errors.abc_id}</span>}
          <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
            Please use the given URL to generate your ABC ID: <a href="https://iiitbac-my.sharepoint.com/:b:/g/personal/application_iiitb_ac_in/IQDai93tp6piQZDk5tuH1L7_AdoVSw9jDTGCLjjpi5uiq5Q?e=sHKF1X" target="_blank" rel="noopener noreferrer">Step-by-Step-ABC-ID-creation</a>
          </small>
        </div>
      </div>
    </div>
  )


});

export default CreateApplicant
